import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  CornerUpLeft,
  FileText,
  Highlighter,
  Image,
  MoreHorizontal,
  Paperclip,
  Trash2,
} from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'
import type { Audience } from './audience-tabs'
import { Avatar } from './avatar'
import { Chip } from './chip'
import { EmailHtml } from './email-html'
import { MarkupText, plainMarkup } from './markup-text'
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuSub, MenuSubTrigger, MenuTrigger } from './menu'
import { Tooltip } from './tooltip'

/**
 * One entry in a turn's "..." menu. `separator` draws a rule before it. With `children` it is a submenu ("Move to…")
 * whose `onSelect` is unused; `hint` is a muted second line (e.g. "the pane this turn is in").
 */
export interface TurnAction {
  label: string
  onSelect: () => void
  disabled?: boolean
  separator?: boolean
  hint?: string
  children?: TurnAction[]
  /** A heading drawn above the submenu's items. */
  heading?: string
}

function TurnActionItem({ a }: { a: TurnAction }) {
  if (a.children?.length) {
    return (
      <MenuSub>
        <MenuSubTrigger disabled={a.disabled}>{a.label}</MenuSubTrigger>
        <MenuContent side="left" align="start" className="min-w-64">
          {a.heading ? <MenuLabel>{a.heading}</MenuLabel> : null}
          {a.children.map((c) => (
            <React.Fragment key={c.label}>
              {c.separator ? <MenuSeparator /> : null}
              <MenuItem disabled={c.disabled} onClick={c.onSelect}>
                <span className="flex flex-col">
                  <span>{c.label}</span>
                  {c.hint ? <span className="text-xs text-muted-foreground">{c.hint}</span> : null}
                </span>
              </MenuItem>
            </React.Fragment>
          ))}
        </MenuContent>
      </MenuSub>
    )
  }
  return (
    <MenuItem disabled={a.disabled} onClick={a.onSelect}>
      {a.hint ? (
        <span className="flex flex-col">
          <span>{a.label}</span>
          <span className="text-xs text-muted-foreground">{a.hint}</span>
        </span>
      ) : (
        a.label
      )}
    </MenuItem>
  )
}

export interface TurnAttachment {
  name: string
  size: string
  kind?: 'image' | 'pdf' | 'file'
}

/** An Outlook reaction to this turn ("Piyush Hasija reacted 👍"), shown as a marker, never as a turn (#174). */
export interface TurnReaction {
  /** Outlook's reaction name: like | heart | laugh | surprised | sad | celebrate | unknown. */
  reaction: string
  by: string
  at?: string
}

const REACTION_GLYPH: Record<string, string> = {
  like: '👍',
  heart: '❤️',
  laugh: '😆',
  surprised: '😮',
  sad: '😢',
  celebrate: '🎉',
}

export function reactionGlyph(reaction: string): string {
  return REACTION_GLYPH[reaction] ?? '👍'
}

/** The small "👍 Piyush Hasija reacted" chips under a turn. */
export function TurnReactions({ reactions, className }: { reactions: TurnReaction[]; className?: string }) {
  if (!reactions.length) return null
  return (
    <div className={cn('mt-2 flex flex-wrap gap-1.5', className)} data-slot="turn-reactions">
      {reactions.map((r, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: one person can react twice
          key={`${r.by}-${i}`}
          title={`${r.by} reacted to this message${r.at ? ` (${r.at})` : ''} in Outlook`}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground"
        >
          <span aria-hidden="true">{reactionGlyph(r.reaction)}</span>
          <span>{r.by} reacted</span>
        </span>
      ))}
    </div>
  )
}

export interface ThreadTurnProps {
  author: { name: string; email?: string; org?: string; riley?: boolean }
  direction: 'inbound' | 'outbound' | 'note'
  /** A technician-authored Desk note visible to the customer (#852). It reads as outbound, not internal. */
  publicNote?: boolean
  audience?: Audience
  to?: string
  /** Cc shown beside To ("to Kyle Logan, cc Rick Curran"). */
  cc?: string
  /** Bcc shown to staff. Absent when Desk did not send one, or the mirror never stored it. */
  bcc?: string
  at: string
  subject?: string
  /** A mailbox-generated response (#415): muted, compact by default; the caller controls collapse state. */
  autoReply?: { label: string; returnDate?: string }
  /** Robot mail (noreply@, alerts@; #727): collapsed by default ("Automated email · show"), no inline-answer marks. */
  automated?: boolean
  /** Clean text, quoted trail already stripped; paragraphs separated by blank lines. */
  body: string
  /**
   * The email's sanitized HTML (#209). When set it is rendered (formatting, inline-image thumbnails, the quoted trail
   * collapsed) instead of `body`; `body` stays the text every other surface uses.
   */
  html?: string
  /** A Desk draft that was never sent. Never counts as settling the turn. */
  draft?: { savedAt: string }
  attachments?: TurnAttachment[]
  /** Inline highlights the customer made inside our quoted email, preserved and marked. */
  inlineAnswers?: string[]
  /** Desk mention ids in `body` (`zsu[@user:<zuid>]zsu`) resolved to names; an absent id renders "@unknown teammate". */
  mentions?: Record<string, string>
  label?: React.ReactNode
  /** The reply arrow (sent turns). Without a handler the arrow is not drawn: a button that does nothing is a bug. */
  onReply?: () => void
  /** The trash icon on a draft. Without a handler the icon is not drawn. */
  onDiscardDraft?: () => void
  /** What the trash icon's tooltip says happens (Riley's ownership check, or your own Desk login). */
  discardTitle?: string
  /** The "..." menu. Empty or absent: no menu button. */
  actions?: TurnAction[]
  /** Outlook reactions to this turn, drawn as small markers under it. */
  reactions?: TurnReaction[]
  /** Extra content under the body (e.g. live attachment chips that open through the app). */
  children?: React.ReactNode
  /**
   * Collapsible turns (#220). With `onToggleCollapsed` the header toggles the turn; `collapsed` draws one line:
   * author, time, a one-line preview and badges for attachments, images, drafts and highlights.
   */
  collapsed?: boolean
  onToggleCollapsed?: () => void
  /** Attachments the caller renders itself (e.g. live chips in `children`), counted in the collapsed badge. */
  fileCount?: number
}

/**
 * Compact, accessible message-direction marker shared by desktop and phone timelines (#853).
 * The arrow, placement and colour reinforce one another; the tooltip and screen-reader label make
 * direction explicit without adding persistent header text to every message.
 */
export function MessageDirectionCue({ direction, focusable = true }: { direction: 'inbound' | 'outbound'; focusable?: boolean }) {
  const inbound = direction === 'inbound'
  const label = inbound ? 'Inbound' : 'Outbound'
  const Icon = inbound ? ArrowDownLeft : ArrowUpRight
  return (
    <Tooltip content={`${label} message`}>
      <span
        tabIndex={focusable ? 0 : undefined}
        role="img"
        aria-label={`${label} message`}
        data-slot="message-direction"
        data-direction={direction}
        className={cn(
          'inline-flex size-6 shrink-0 items-center justify-center rounded-full border',
          inbound ? 'border-primary/30 bg-primary-soft text-primary' : 'border-success/30 bg-success-soft text-success',
        )}
      >
        <Icon className="size-3.5" aria-hidden="true" />
      </span>
    </Tooltip>
  )
}

function detectedReturnLabel(returnDate?: string): string | null {
  const match = returnDate?.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const [, year, month, day] = match
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/** Why an OOO turn is compact, including its detected return date even if an older API label omitted it (#854). */
export function automaticReplyCollapseLabel(autoReply: { label: string; returnDate?: string }): string {
  let reason = autoReply.label.trim().replace(/^Automatic reply/i, 'automatic reply') || 'automatic reply'
  const back = detectedReturnLabel(autoReply.returnDate)
  if (back && !/\bback\b/i.test(reason)) reason += ` · back ${back}`
  return `Collapsed: ${reason}`
}

/** The one-line preview of a collapsed turn: its first non-empty line, markup resolved to plain text. */
export function turnPreview(body: string, mentions?: Record<string, string>): string {
  const text = plainMarkup(body, mentions)
  const line = text.split('\n').find((l) => l.trim()) ?? ''
  return line.trim()
}

const AUTOMATED_LABEL = 'Automated email · show'

function hasInlineImage(html?: string): boolean {
  return Boolean(html && /<img\b/i.test(html))
}

/** One line for the turn header: "to Kyle Logan, cc Rick Curran, bcc Alex Kim". */
export function recipientSummary(to?: string, cc?: string, bcc?: string): string | null {
  const parts = [to ? `to ${to}` : '', cc ? `cc ${cc}` : '', bcc ? `bcc ${bcc}` : ''].filter(Boolean)
  return parts.length ? parts.join(', ') : null
}

/** Don't toggle when the click was on a control inside the header (reply, discard, the "..." menu, a link). */
function fromControl(e: React.MouseEvent): boolean {
  const el = e.target as HTMLElement | null
  return Boolean(el?.closest('button, a, input, [role="menuitem"], [role="menu"], [data-slot="message-direction"]'))
}

/**
 * One turn of a thread. A Desk draft renders as a dashed box labelled `DRAFT - not sent`
 * (disclosure rule 7) so nobody mistakes it for an answer the customer has.
 */
export function ThreadTurn({
  author,
  direction,
  publicNote = false,
  audience = 'customer',
  to,
  cc,
  bcc,
  at,
  subject,
  autoReply,
  automated,
  body,
  html,
  draft,
  attachments,
  inlineAnswers,
  mentions,
  label,
  onReply,
  onDiscardDraft,
  discardTitle,
  actions,
  reactions,
  children,
  collapsed = false,
  onToggleCollapsed,
  fileCount = 0,
}: ThreadTurnProps) {
  const paragraphs = body.split(/\n{2,}/)
  // Typed-in answers inside our quoted email: above the fold, so the fold never hides them (#225).
  const answers = inlineAnswers?.length ? (
    <div className="mt-2 space-y-1 border-l-2 border-warning pl-3" data-slot="inline-answers">
      <p className="text-xs font-medium text-warning">Answered inline in our quoted email</p>
      {inlineAnswers.map((a) => (
        <p key={a} className="text-sm">
          <mark className="rounded-xs bg-warning-soft px-0.5 text-foreground">{a}</mark>
        </p>
      ))}
    </div>
  ) : null
  const files = (attachments?.length ?? 0) + fileCount
  const recipients = recipientSummary(to, cc, bcc)
  const sentDirection = draft ? null : publicNote ? 'outbound' : direction === 'note' ? null : direction
  const directionLabel = sentDirection ? `${sentDirection === 'inbound' ? 'Inbound' : 'Outbound'} message` : null
  const autoReplyCollapsed = autoReply ? automaticReplyCollapseLabel(autoReply) : null
  if (collapsed && onToggleCollapsed) {
    const actionLabel = autoReplyCollapsed
      ? `Expand ${autoReplyCollapsed.replace(/^Collapsed: /, '')} from ${author.name}, ${at}`
      : `Expand turn from ${author.name}, ${draft ? `saved ${draft.savedAt}` : at}`
    const collapsedControl = (
      <button
        type="button"
        aria-expanded={false}
        aria-label={directionLabel ? `${actionLabel}. ${directionLabel}` : actionLabel}
        onClick={onToggleCollapsed}
        className="flex w-full min-w-0 cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm hover:bg-muted/50"
      >
        <ChevronRight className="size-4 shrink-0 text-subtle" />
        {sentDirection ? <MessageDirectionCue direction={sentDirection} focusable={false} /> : null}
        <Avatar name={author.name} riley={author.riley} size="sm" />
        <span className="shrink-0 font-semibold">{author.name}</span>
        {recipients ? (
          <span className="max-w-[40%] shrink truncate text-xs text-subtle" title={recipients}>
            {recipients}
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate text-muted-foreground" data-slot="turn-preview">
          {autoReplyCollapsed ?? (automated ? AUTOMATED_LABEL : turnPreview(body, mentions))}
        </span>
        {draft ? (
          <Chip tone="primary" className="shrink-0 font-semibold uppercase tracking-wide">
            DRAFT - not sent
          </Chip>
        ) : null}
        {inlineAnswers?.length ? (
          <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-warning" title="Answered inline">
            <Highlighter className="size-3.5" aria-hidden="true" />
            <span className="sr-only">highlights</span>
            {inlineAnswers.length}
          </span>
        ) : null}
        {hasInlineImage(html) ? (
          <span className="inline-flex shrink-0 items-center text-xs text-subtle" title="Has images">
            <Image className="size-3.5" aria-hidden="true" />
            <span className="sr-only">images</span>
          </span>
        ) : null}
        {files ? (
          <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-subtle" title={`${files} attachment(s)`}>
            <Paperclip className="size-3.5" aria-hidden="true" />
            <span className="sr-only">attachments</span>
            {files}
          </span>
        ) : null}
        <time className="shrink-0 text-xs text-subtle">{draft ? `Saved ${draft.savedAt}` : at}</time>
      </button>
    )
    return (
      <article
        data-direction={direction}
        data-draft={draft ? 'true' : undefined}
        data-collapsed="true"
        aria-label={
          draft
            ? `Draft, not sent, ${recipients ?? `to ${to ?? 'recipient'}`}`
            : autoReplyCollapsed
              ? `${author.name}, ${at}, ${autoReplyCollapsed}`
              : `${author.name}, ${at}`
        }
        className={cn(
          'rounded-lg border bg-card',
          draft ? 'border-2 border-dashed border-border-strong bg-muted/40' : 'border-border',
          (autoReply || automated) && 'bg-muted/40 text-muted-foreground',
          direction === 'note' && !publicNote && !draft && 'border-audience-internal/20 bg-audience-internal-soft/50',
          sentDirection === 'inbound' && 'mr-6 border-l-4 border-l-primary/60',
          sentDirection === 'outbound' && 'ml-6 border-r-4 border-r-success/60 bg-success-soft/15',
        )}
      >
        {directionLabel ? <Tooltip content={directionLabel}>{collapsedControl}</Tooltip> : collapsedControl}
      </article>
    )
  }
  return (
    <article
      data-direction={direction}
      data-draft={draft ? 'true' : undefined}
      aria-label={draft ? `Draft, not sent, ${recipients ?? `to ${to ?? 'recipient'}`}` : `${author.name}, ${at}`}
      className={cn(
        'rounded-lg border bg-card px-4 py-3',
        draft ? 'border-2 border-dashed border-border-strong bg-muted/40' : 'border-border',
        (autoReply || automated) && 'bg-muted/40 text-muted-foreground',
        direction === 'note' && !publicNote && !draft && 'border-audience-internal/20 bg-audience-internal-soft/50',
        sentDirection === 'inbound' && 'mr-6 border-l-4 border-l-primary/60',
        sentDirection === 'outbound' && 'ml-6 border-r-4 border-r-success/60 bg-success-soft/15',
      )}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: a mouse shortcut over the header; the Collapse chevron button is the keyboard path. */}
      <div
        className={cn(
          'grid min-w-0 items-start gap-x-3 gap-y-2 sm:flex sm:gap-3',
          sentDirection ? 'grid-cols-[auto_auto_minmax(0,1fr)]' : 'grid-cols-[auto_minmax(0,1fr)]',
          onToggleCollapsed && 'cursor-pointer',
        )}
        onClick={onToggleCollapsed ? (e) => !fromControl(e) && onToggleCollapsed() : undefined}
      >
        {sentDirection ? <MessageDirectionCue direction={sentDirection} /> : null}
        <Avatar name={author.name} riley={author.riley} />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
            {draft ? (
              <Chip tone="primary" className="font-semibold uppercase tracking-wide">
                DRAFT - not sent
              </Chip>
            ) : null}
            {autoReply ? <Chip className="text-muted-foreground">{autoReply.label}</Chip> : null}
            {automated && !autoReply ? <Chip className="text-muted-foreground">Automated email</Chip> : null}
            <span className="min-w-0 break-words font-semibold [overflow-wrap:anywhere]">{author.name}</span>
            {author.org ? (
              <span className="min-w-0 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">{author.org}</span>
            ) : null}
            {author.email ? (
              <span className="min-w-0 break-words text-xs text-subtle [overflow-wrap:anywhere]">&lt;{author.email}&gt;</span>
            ) : null}
            {label}
          </div>
          {recipients ? <p className="min-w-0 break-words text-xs text-subtle [overflow-wrap:anywhere]">{recipients}</p> : null}
        </div>
        <div className="col-span-full flex min-w-0 items-center justify-end gap-1 text-xs text-subtle sm:col-span-1 sm:ml-auto sm:shrink-0">
          <time>{draft ? `Saved ${draft.savedAt}` : at}</time>
          {draft && onDiscardDraft ? (
            <button
              type="button"
              aria-label="Discard draft"
              title={discardTitle ?? 'Delete this unsent draft'}
              onClick={onDiscardDraft}
              className="cursor-pointer rounded p-1 hover:bg-muted"
            >
              <Trash2 className="size-4" />
            </button>
          ) : !draft && direction !== 'note' && onReply ? (
            <button
              type="button"
              aria-label={audience === 'internal' ? 'Reply with an internal note' : 'Reply'}
              title={audience === 'internal' ? 'Reply with a private internal note (never public)' : 'Reply (R)'}
              onClick={onReply}
              className="cursor-pointer rounded p-1 hover:bg-muted"
            >
              <CornerUpLeft className="size-4" />
            </button>
          ) : null}
          {onToggleCollapsed ? (
            <button
              type="button"
              aria-expanded={true}
              aria-label="Collapse turn"
              title="Collapse"
              onClick={onToggleCollapsed}
              className="cursor-pointer rounded p-1 hover:bg-muted"
            >
              <ChevronDown className="size-4" />
            </button>
          ) : null}
          {actions?.length ? (
            <Menu>
              <MenuTrigger aria-label="More" className="cursor-pointer rounded p-1 hover:bg-muted">
                <MoreHorizontal className="size-4" />
              </MenuTrigger>
              <MenuContent>
                {actions.map((a) => (
                  <React.Fragment key={a.label}>
                    {a.separator ? <MenuSeparator /> : null}
                    <TurnActionItem a={a} />
                  </React.Fragment>
                ))}
              </MenuContent>
            </Menu>
          ) : null}
        </div>
      </div>
      <div className="mt-2 min-w-0 pl-0 sm:pl-11">
        {subject ? <p className="mb-1 font-semibold">{subject}</p> : null}
        {html ? (
          <div data-audience={audience}>
            <EmailHtml html={html} mentions={mentions} aboveFold={answers} automated={automated} />
          </div>
        ) : (
          <div className="email-prose text-base text-foreground/90" data-audience={audience} data-slot="email-prose">
            {paragraphs.map((p, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: paragraphs are positional
              <p key={i} className="whitespace-pre-line">
                <MarkupText text={p} mentions={mentions} />
              </p>
            ))}
          </div>
        )}
        {html ? null : answers}
        {attachments?.length ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {attachments.map((a) => (
              <span
                key={a.name}
                className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm"
              >
                <FileText className="size-4 text-muted-foreground" />
                <span>{a.name}</span>
                <span className="text-xs text-subtle">{a.size}</span>
              </span>
            ))}
          </div>
        ) : null}
        {reactions?.length ? <TurnReactions reactions={reactions} /> : null}
        {children}
      </div>
    </article>
  )
}
